import React, { useRef, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { SERVICE_ACTIONS, PAYMENT_MODES, toDateTimeInputValue } from "../shared/vehicleFleetConstants";

const emptyPartRow = () => ({ partName: "", serviceAction: SERVICE_ACTIONS[0], cost: "" });

// Closes out a "Started" service - mirrors serviceEnd()'s chain exactly
// (non-empty part/cost lines, non-negative totalCost/vehicleKm, a paymentBy
// and an end date/time), same validation ServiceDetail.js's old inline form
// used to run before this was pulled into its own modal.
const ServiceEndModal = ({ service, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);

    const [partRows, setPartRows] = useState([emptyPartRow()]);
    const [totalCost, setTotalCost] = useState("");
    const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES[0]);
    const [paymentBy, setPaymentBy] = useState("");
    const [vehicleKm, setVehicleKm] = useState("");
    const [servicedEndAt, setServicedEndAt] = useState(toDateTimeInputValue(new Date()));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updatePartRow = (index, field, value) => setPartRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    const addPartRow = () => setPartRows((prev) => [...prev, emptyPartRow()]);
    const removePartRow = (index) => setPartRows((prev) => prev.filter((_, i) => i !== index));

    const onSubmit = async () => {
        const cleaned = partRows.filter((r) => r.partName.trim() || String(r.cost).trim());
        if (String(totalCost).trim() === "" || Number(totalCost) < 0) {
            toast.error("A non-negative total cost is required");
            return;
        }
        if (!cleaned.length) {
            toast.error("Add at least 1 part/service line");
            return;
        }
        const invalid = cleaned.some((r) => !r.partName.trim() || String(r.cost).trim() === "" || Number(r.cost) < 0);
        if (invalid) {
            toast.error("Each line needs a part name and a non-negative cost");
            return;
        }
        if (!paymentBy.trim()) {
            toast.error("Paid by is required");
            return;
        }
        if (String(vehicleKm).trim() === "" || Number(vehicleKm) < 0) {
            toast.error("A non-negative vehicle odometer reading is required");
            return;
        }
        if (!servicedEndAt) {
            toast.error("Service end date/time is required");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/service/${service?.serviceId}/end`,
            data: {
                totalCost: Number(totalCost),
                serviceDetails: cleaned.map((r) => ({ partName: r.partName.trim(), serviceAction: r.serviceAction, cost: Number(r.cost) })),
                paymentMode,
                paymentBy: paymentBy.trim(),
                vehicleKm: Number(vehicleKm),
                servicedEndAt: new Date(servicedEndAt).toISOString(),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Service closed successfully");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to close service"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">End Service</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="fw-semibold small mb-2">Parts / services</div>
                        <div className="row g-2 mb-3">
                            {partRows.map((row, index) => (
                                <div key={index} className="col-12">
                                    <div className="row g-2 align-items-center">
                                        <div className="col-12 col-md-5">
                                            <input type="text" className="form-control" placeholder="Part / service name" value={row.partName} onChange={(e) => updatePartRow(index, "partName", e.target.value)} />
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <select className="form-select" value={row.serviceAction} onChange={(e) => updatePartRow(index, "serviceAction", e.target.value)}>
                                                {SERVICE_ACTIONS.map((a) => (<option key={a} value={a}>{a}</option>))}
                                            </select>
                                        </div>
                                        <div className="col-5 col-md-3">
                                            <input type="number" className="form-control" placeholder="Cost" min="0" value={row.cost} onChange={(e) => updatePartRow(index, "cost", e.target.value)} />
                                        </div>
                                        <div className="col-1">
                                            {partRows.length > 1 ? (
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removePartRow(index)}>&times;</button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <div className="col-12">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addPartRow}>+ Add line</button>
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-md-4">
                                <label className="form-label">Total cost</label>
                                <input type="number" className="form-control" min="0" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} />
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Payment mode</label>
                                <select className="form-select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                    {PAYMENT_MODES.map((p) => (<option key={p} value={p}>{p}</option>))}
                                </select>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="form-label">Paid by</label>
                                <input type="text" className="form-control" value={paymentBy} onChange={(e) => setPaymentBy(e.target.value)} placeholder="Name / reference" />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Odometer reading</label>
                                <input type="number" className="form-control" min="0" value={vehicleKm} onChange={(e) => setVehicleKm(e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6">
                                <label className="form-label">Ended at</label>
                                <input type="datetime-local" className="form-control" value={servicedEndAt} onChange={(e) => setServicedEndAt(e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : "Close service"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceEndModal;
